import React, { useState, useCallback, useEffect, useRef } from "react";

import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { IoMdArrowBack, IoMdArrowForward } from "react-icons/io";
import { toast } from "react-toastify";
import { Field, Formik } from "formik";
import * as Yup from "yup";

import ContainedButton from "components/layout/form/containedButton";
import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";

import ResetPassword from "../../AcessarMI/ResetPassword";

import { useMFAContext } from "hooks/useMFA";
import { useAuth } from "hooks/authHook";

import { handleUserId } from "store/actions/user-actions";
import { registerValuesMembership } from "store/actions/membership-actions";
import {
  registerLocation,
  registerValues,
  toggleStep,
} from "store/actions/first-access-mi-actions";
import { getSigninInfo } from "store/actions/signin-actions";

import { sendToken, validationToken } from "../services";
import {
  requestGetAccessToken,
  requestGetCustomer,
  requestGetCustomersWithDocument,
  requestGetCustomersWithUserId,
  requestGetUser,
  requestOauth,
  requestPostUser,
  resetLocalStorage,
  updateUserCount,
} from "services/authServices";

import { HeaderContainer } from "../styles";

import {
  Container,
  ContainerButtons,
  FormContainer,
  TextTimer,
} from "./styles";

const formatTimer = (timer) => {
  let minutes = parseInt(timer / 60, 10);
  let seconds = parseInt(timer % 60, 10);
  minutes = minutes < 10 ? "0" + minutes : minutes;
  seconds = seconds < 10 ? "0" + seconds : seconds;
  return minutes + ":" + seconds;
};

const ValidationToken = ({ setStep, userInfo, contactId = null }) => {
  const [loading, setLoading] = useState();
  const [timer, setTimer] = useState(60);
  const [startTimer, setStartTimer] = useState(false);
  const [showModalResetPass, setShowModalResetPass] = useState(false);

  const { credentials, setCredentials, setAttempts, attempts } =
    useMFAContext();
  const { signIn, setUserInfos } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();

  const intervalRef = useRef();
  let newTimer = 60;

  const decreaseNum = () => {
    if (newTimer === 0) {
      setStartTimer(false);
      clearInterval(intervalRef.current);
      return;
    }
    setTimer((prev) => {
      newTimer = newTimer - 1;
      return prev - 1;
    });
  };

  useEffect(() => {
    if (startTimer) {
      intervalRef.current = setInterval(decreaseNum, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [startTimer]);

  useEffect(() => {
    setStartTimer(true);
  }, []);

  const contact = userInfo.contacts?.find(
    (contact) => contact.contactId === userInfo?.validationContactId
  );

  const resendToken = async () => {
    setLoading(true);
    setAttempts(attempts + 1);

    if (attempts + 1 >= 3) {
      return setStep(1);
    }

    const success = await sendToken(contact, userInfo?.userId, contactId);

    if (success) {
      setTimer(60);
      setStartTimer(true);
    }
    setLoading(false);
  };

  const handleSubmit = useCallback(
    async (values) => {
      if (!loading) {
        try {
          setLoading(true);

          const { token } = values;

          setCredentials({ ...credentials, token });

          resetLocalStorage();

          let accessToken = await requestGetAccessToken();

          const { login, password } = credentials;

          const reponseOauth = await requestOauth(
            login,
            password,
            token,
            accessToken,
            dispatch,
            getSigninInfo
          );

          accessToken = reponseOauth.access_token;

          const responseGetUser = await requestGetUser(
            login,
            accessToken,
            dispatch,
            handleUserId
          );

          const responseCustomer = await requestGetCustomersWithDocument(
            accessToken,
            login,
            dispatch,
            registerValues,
            registerValuesMembership
          );

          if (
            Object.keys(responseGetUser).length === 0 &&
            responseCustomer &&
            responseCustomer.customerId
          ) {
            dispatch(
              registerValues({
                document: login,
                customerId: responseCustomer.customerId,
                customerCode: responseCustomer.customerCode,
              })
            );

            await requestPostUser(
              accessToken,
              responseCustomer.customerCode,
              login,
              dispatch,
              registerLocation,
              toggleStep,
              registerValues,
              history
            );
            return null;
          }

          const responseGetCustomer = await requestGetCustomersWithUserId(
            responseGetUser.userId,
            accessToken
          );

          const customerAddresses = responseGetCustomer?.customerAddresses[0];

          signIn({
            loginEmail: responseGetCustomer.email,
            accessToken,
            refreshToken: reponseOauth.refresh_token,
            customerType: "MI",
          });

          setUserInfos({
            city: customerAddresses.city,
            stateCode: customerAddresses.stateCode,
            customerName: responseGetCustomer.corporateName,
            customerCode: responseGetCustomer.customerCode,
            userName: responseGetCustomer?.corporateName,
            document: login,
            customerId: responseGetCustomer.customerId,
            customerSiteId: responseGetCustomer.customerSiteId,
            customerSiteCode: responseGetCustomer.customerSiteId,
            customerAddressLine: customerAddresses.addressLine,
            userCustomerRelationshipId: null,
            userId: responseGetUser.userId,
            commercialRegion: responseGetCustomer.commercialRegion,
            commercialComplement:
              responseGetCustomer.customerAddresses &&
              responseGetCustomer.customerAddresses.length > 0
                ? responseGetCustomer.customerAddresses[0].complement
                : "",
          });

          await requestGetCustomer(
            responseGetCustomer.customerSiteId,
            accessToken
          );
          await updateUserCount(login, accessToken);

          const search = history?.location?.search
            ? history.location.search.split("=")
            : null;

          if (search && search.length > 1) {
            history.push(`/${search[1]}`);
          } else {
            history.push("/portal");
          }

          setLoading(false);
        } catch (error) {
          console.log({ error });
          setLoading(false);
          const erro = error?.response;

          const code = error?.response?.data?.code;
          const message = error?.response?.data?.message;
          const status = erro?.status;

          if (status === 401) {
            if (code === "401.004") {
              setShowModalResetPass(true);
            } else if (code === "401.006") {
              toast.warn(message);
            } else if (code === "401.005") {
              toast.error(message);
            } else {
              toast.error(message);
            }
          }

          if (status === 400) {
            toast.error("Autenticação inválida");
            return null;
          }
          if (erro) {
            if (status !== 401) {
              toast.error(message);
            }
          } else if (status !== 401) {
            toast.error("Ocorreu um erro interno na aplicação");
          }
        }

        return null;
      }
    },
    [validationToken, loading]
  );

  const validatorSchema = () =>
    Yup.object().shape({
      token: Yup.string().required("É necessário inserir o token"),
    });

  return (
    <Container>
      <HeaderContainer>
        <h1>Insira o código para continuar com o login</h1>
        <h2>Informe o código recebido</h2>
      </HeaderContainer>

      <Formik
        initialValues={{
          token: "",
        }}
        validationSchema={validatorSchema}
        onSubmit={handleSubmit}
        render={({ handleChange, handleBlur, values, errors, touched }) => (
          <FormContainer>
            <Field
              name="token"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Inserir código"
                  placeholder="Inserir código"
                  errorCondition={errors.token && touched.token}
                  errorMessage={errors.token}
                  isCorrect={!errors.token}
                  value={values.token}
                  onBlur={handleBlur}
                  onClick={() => toast.dismiss()}
                  onChange={handleChange}
                />
              )}
            />
            <TextTimer>
              Não recebi o código.{" "}
              {timer > 0 ? (
                `Aguarde ${formatTimer(timer)}`
              ) : (
                <a href="#" onClick={resendToken}>
                  Enviar novamente
                </a>
              )}
            </TextTimer>
            <ContainerButtons>
              <ContainedButton
                block
                type="submit"
                continue
                loading={loading}
                disabled={loading || !values.token}
                id="Avançar"
                width="100%"
                TrailingIcon={() => <IoMdArrowForward size={20} />}
              >
                Validar código
              </ContainedButton>
              <OutlinedButton
                width="100%"
                LeadingIcon={() => <IoMdArrowBack size={20} />}
                onClick={() => setStep(1)}
              >
                Voltar
              </OutlinedButton>
            </ContainerButtons>
          </FormContainer>
        )}
      />
      <ResetPassword
        setShowModal={setShowModalResetPass}
        showModal={showModalResetPass}
      />
    </Container>
  );
};

export default ValidationToken;
